// REFACTORED: 2026-02-06
// FROM src/components/UserFeedbackPage.tsx

import React, { useCallback, useRef, useState } from 'react';
import { InlineSpinner, ListLinesSkeleton } from '../components/common/loading';

import {
  Alert,
  alpha,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnFilter } from '../components/filters/ColumnFilter';
import { useAuth } from '../contexts/AuthContext';
import { USER_ROLES } from '../contexts/AuthContext';
import { submitUserFeedback, getAllUserFeedback, resolveUserFeedback, UserFeedbackItem } from '../services/api';
import { SEARCE_COLORS } from '../theme';
import RichTextEditor from '../components/common/RichTextEditor';
import ViewFeedbackModal from '../components/userFeedback/ViewFeedbackModal';
import { getApiErrorMessage } from '../utils/apiErrorUtils';

const MAX_REFERENCE_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB

/** Plain text length from HTML (strip tags) for validation */
const getTextLengthFromHtml = (html: string): number => {
  if (!html || !html.trim()) return 0;
  const stripped = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return stripped.length;
};

/** Strip HTML to plain text for table preview */
const stripHtml = (html: string): string => {
  if (!html || !html.trim()) return '';
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
};

/** Format date as Searce standard: dd-mmm-yyyy (e.g. 10-Mar-2025) */
const formatDate = (iso: string) => {
  try {
    const d = new Date(iso);
    const day = String(d.getDate()).padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return iso;
  }
};

/** Resolved timestamp for table display (local time). */
const formatDateTime = (iso: string) => {
  try {
    const d = new Date(iso);
    const day = String(d.getDate()).padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    const h = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year} ${h}:${min}`;
  } catch {
    return iso;
  }
};

const TRIM_LENGTH = 20;
const trimWithEllipsis = (text: string, maxLen: number = TRIM_LENGTH) => {
  if (!text || !String(text).trim()) return '';
  const s = String(text).trim();
  return s.length <= maxLen ? s : s.slice(0, maxLen) + '…';
};

interface FeedbackFormData {
  category: string;
  subject: string;
  feedback: string;
}

const UserFeedbackPage: React.FC = () => {
  const { user, hasAnyRole } = useAuth();
  const isAdminOrSuperAdmin = hasAnyRole([USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN]);

  const [formData, setFormData] = useState<FeedbackFormData>({
    category: '',
    subject: '',
    feedback: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [referenceFiles, setReferenceFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [viewingFeedback, setViewingFeedback] = useState<UserFeedbackItem | null>(null);
  const [feedbackPage, setFeedbackPage] = useState(0);
  const [feedbackRowsPerPage, setFeedbackRowsPerPage] = useState(10);
  const [feedbackFilterSearch, setFeedbackFilterSearch] = useState<string>('');
  const [feedbackFilterMenus, setFeedbackFilterMenus] = useState<Record<string, { anchorEl: HTMLElement | null; selectedValues: string[] }>>({});
  const [listActionError, setListActionError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: allFeedback = [], isLoading: loadingList, refetch: refetchList } = useQuery({
    queryKey: ['user-feedback-list'],
    queryFn: getAllUserFeedback,
    enabled: isAdminOrSuperAdmin && activeTab === 1,
  });

  const resolveMutation = useMutation({
    mutationFn: resolveUserFeedback,
    onSuccess: (data, id) => {
      setListActionError(null);
      if (data.success && data.resolved_at) {
        queryClient.setQueryData<UserFeedbackItem[]>(['user-feedback-list'], (old) =>
          (old ?? []).map((f) => (f.id === id ? { ...f, resolved_at: data.resolved_at } : f))
        );
        setViewingFeedback((prev) => (prev?.id === id ? { ...prev, resolved_at: data.resolved_at } : prev));
      } else if (!data.success) {
        setListActionError('Could not resolve this feedback.');
      } else {
        refetchList();
      }
    },
    onError: (err: unknown) => {
      setListActionError(getApiErrorMessage(err, 'Could not resolve this feedback. Please try again.'));
    },
  });

  const feedbackCategories = [
    'Bug Report',
    'Feature Request',
    'Performance Issue',
    'Data Accuracy',
    'General Feedback',
    'Other',
  ];

  const FEEDBACK_TABLE_COLUMNS = [
    { id: 'created', label: 'Created', filterable: true },
    { id: 'user', label: 'User', filterable: true },
    { id: 'category', label: 'Category', filterable: true },
    { id: 'subject', label: 'Subject', filterable: true },
    { id: 'feedback', label: 'Feedback', filterable: false },
    { id: 'resolved', label: 'Resolved', filterable: false },
    { id: 'actions', label: 'Actions', filterable: false },
  ] as const;

  const getUniqueValuesForColumn = useCallback((columnId: string, data: UserFeedbackItem[]): string[] => {
    const set = new Set<string>();
    data.forEach((r) => {
      if (columnId === 'created') set.add(formatDate(r.submitted_at));
      else if (columnId === 'user') set.add(r.user_name || '');
      else if (columnId === 'category') set.add(r.category || '');
      else if (columnId === 'subject') set.add(r.subject || '');
    });
    return Array.from(set).filter(Boolean).sort();
  }, []);

  /** Rows for chip options: global search + all column filters except `excludeColumnId`. */
  const getFeedbackRowsForColumnOptions = useCallback(
    (excludeColumnId: string): UserFeedbackItem[] => {
      let list = allFeedback;
      if (feedbackFilterSearch.trim()) {
        const q = feedbackFilterSearch.trim().toLowerCase();
        list = list.filter(
          (r) =>
            r.subject.toLowerCase().includes(q) ||
            r.user_name.toLowerCase().includes(q) ||
            r.user_email.toLowerCase().includes(q) ||
            r.category.toLowerCase().includes(q) ||
            stripHtml(r.feedback).toLowerCase().includes(q)
        );
      }
      FEEDBACK_TABLE_COLUMNS.forEach((col) => {
        if (!col.filterable || col.id === excludeColumnId) return;
        const menu = feedbackFilterMenus[col.id];
        const selected = menu?.selectedValues ?? [];
        if (selected.length === 0) return;
        list = list.filter((r) => {
          const val =
            col.id === 'created'
              ? formatDate(r.submitted_at)
              : col.id === 'user'
                ? r.user_name || ''
                : col.id === 'category'
                  ? r.category || ''
                  : r.subject || '';
          return selected.includes(val);
        });
      });
      return list;
    },
    [allFeedback, feedbackFilterSearch, feedbackFilterMenus]
  );

  const handleFeedbackFilterClick = (columnId: string, event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setFeedbackFilterMenus((prev) => ({
      ...prev,
      [columnId]: {
        anchorEl: event.currentTarget,
        selectedValues: prev[columnId]?.selectedValues ?? [],
      },
    }));
  };

  const handleFeedbackFilterClose = (columnId: string) => {
    setFeedbackFilterMenus((prev) => ({
      ...prev,
      [columnId]: { ...prev[columnId], anchorEl: null },
    }));
  };

  const handleFeedbackFilterSelect = (columnId: string, selectedValues: string[]) => {
    setFeedbackFilterMenus((prev) => ({
      ...prev,
      [columnId]: { ...prev[columnId], selectedValues, anchorEl: null },
    }));
    setFeedbackPage(0);
  };

  const handleClearAllFeedbackFilters = () => {
    setFeedbackFilterSearch('');
    setFeedbackFilterMenus({});
    setFeedbackPage(0);
  };

  const hasAnyFeedbackFilterActive = (): boolean => {
    if (feedbackFilterSearch.trim()) return true;
    return Object.values(feedbackFilterMenus).some((m) => m?.selectedValues?.length > 0);
  };

  const filteredFeedback = React.useMemo(() => {
    let list = allFeedback;
    if (feedbackFilterSearch.trim()) {
      const q = feedbackFilterSearch.trim().toLowerCase();
      list = list.filter(
        (r) =>
          r.subject.toLowerCase().includes(q) ||
          r.user_name.toLowerCase().includes(q) ||
          r.user_email.toLowerCase().includes(q) ||
          r.category.toLowerCase().includes(q) ||
          stripHtml(r.feedback).toLowerCase().includes(q)
      );
    }
    FEEDBACK_TABLE_COLUMNS.forEach((col) => {
      if (!col.filterable) return;
      const menu = feedbackFilterMenus[col.id];
      const selected = menu?.selectedValues ?? [];
      if (selected.length === 0) return;
      list = list.filter((r) => {
        const val =
          col.id === 'created'
            ? formatDate(r.submitted_at)
            : col.id === 'user'
              ? r.user_name || ''
              : col.id === 'category'
                ? r.category || ''
                : r.subject || '';
        return selected.includes(val);
      });
    });
    return list;
  }, [allFeedback, feedbackFilterSearch, feedbackFilterMenus]);

  const paginatedFeedback = React.useMemo(() => {
    const start = feedbackPage * feedbackRowsPerPage;
    return filteredFeedback.slice(start, start + feedbackRowsPerPage);
  }, [filteredFeedback, feedbackPage, feedbackRowsPerPage]);

  const handleFeedbackPageChange = (_: unknown, newPage: number) => setFeedbackPage(newPage);
  const handleFeedbackRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFeedbackRowsPerPage(parseInt(e.target.value, 10));
    setFeedbackPage(0);
  };

  const handleReferenceFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const next: File[] = [];
    const overSize: string[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.size > MAX_REFERENCE_FILE_SIZE_BYTES) {
        overSize.push(`${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
      } else {
        next.push(file);
      }
    }
    if (overSize.length > 0) {
      setError(`Max size 10 MB per file. Skipped: ${overSize.join(', ')}`);
    }
    setReferenceFiles((prev) => [...prev, ...next]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (overSize.length === 0 && error) setError(null);
  };

  const removeReferenceFile = (index: number) => {
    setReferenceFiles((prev) => prev.filter((_, i) => i !== index));
    if (error) setError(null);
  };

  const handleChange = (field: keyof FeedbackFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { value: any } }
  ) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (error) setError(null);
    if (success) setSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!formData.category) {
      setError('Please select a category');
      return;
    }
    if (!formData.subject.trim()) {
      setError('Please enter a subject');
      return;
    }
    const feedbackTextLength = getTextLengthFromHtml(formData.feedback);
    if (feedbackTextLength === 0) {
      setError('Please enter your feedback');
      return;
    }
    if (feedbackTextLength < 10) {
      setError('Feedback must be at least 10 characters long');
      return;
    }

    setLoading(true);

    try {
      await submitUserFeedback(
        {
          user_email: user?.email || '',
          user_name: user?.name || '',
          category: formData.category,
          subject: formData.subject.trim(),
          feedback: formData.feedback.trim(),
        },
        referenceFiles.length > 0 ? referenceFiles : undefined
      );

      setSuccess(true);
      setFormData({
        category: '',
        subject: '',
        feedback: '',
      });
      setReferenceFiles([]);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to submit feedback. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 5, maxWidth: 1200, mx: 'auto', borderRadius: '25px' }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.75rem' }, background: `linear-gradient(135deg, ${SEARCE_COLORS.primary} 0%, #42a5f5 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', mb: 1, letterSpacing: '-0.01em', lineHeight: 1.2 }}>
            Submit Your Feedback for Portal
        </Typography>
        <Typography variant="body2" component="div" sx={{ color: '#252524', fontWeight: 350, fontSize: '0.875rem', mb: 4 }}>
           Share your thoughts and help us improve. Submit feedback, report issues, or suggest enhancements for the portal. Your input helps us continuously refine the platform to better serve your needs and create a more effective experience for everyone at Searce.        
        </Typography>

        {isAdminOrSuperAdmin && (
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(Number(v))} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tab label="Submit feedback" id="user-feedback-tab-0" aria-controls="user-feedback-panel-0" />
            <Tab label="All requests" id="user-feedback-tab-1" aria-controls="user-feedback-panel-1" />
          </Tabs>
        )}

        {activeTab === 0 && (
          <>
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Thank you for your feedback! Your submission has been received and will be reviewed by our team.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth required sx={{ '& .MuiOutlinedInput-root': { borderRadius: '25px' } }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => handleChange('category')({ target: { value: e.target.value } })}
                  label="Category"
                >
                  {feedbackCategories.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                required
                label="Subject"
                value={formData.subject}
                onChange={handleChange('subject')}
                placeholder="Brief summary of your feedback"
                variant="outlined"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '25px' } }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5, color: 'text.primary' }}>
                Feedback <span style={{ color: 'red' }}>*</span>
              </Typography>
              <RichTextEditor
                value={formData.feedback}
                onChange={(value) => {
                  setFormData((prev) => ({ ...prev, feedback: value }));
                  if (error) setError(null);
                  if (success) setSuccess(false);
                }}
                placeholder="Please provide detailed feedback..."
                helperText={`${getTextLengthFromHtml(formData.feedback)} characters (minimum 10)`}
                minHeight={280}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                References
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                Optional. Upload multiple files (e.g. screenshots, PDFs, documents). Max 10 MB per file.
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleReferenceFilesChange}
                style={{ display: 'none' }}
              />
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                sx={{ mb: 1 }}
              >
                Choose files
              </Button>
              {referenceFiles.length > 0 && (
                <List dense sx={{ bgcolor: 'action.hover', borderRadius: 1, mt: 1, maxHeight: 200, overflow: 'auto' }}>
                  {referenceFiles.map((file, index) => (
                    <ListItem key={`${file.name}-${file.size}-${index}`}>
                      <ListItemText
                        primary={file.name}
                        secondary={`${(file.size / 1024).toFixed(1)} KB`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      <ListItemSecondaryAction>
                        <IconButton edge="end" size="small" onClick={() => removeReferenceFile(index)} aria-label="Remove">
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setFormData({
                      category: '',
                      subject: '',
                      feedback: '',
                    });
                    setReferenceFiles([]);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                    setError(null);
                    setSuccess(false);
                  }}
                  disabled={loading}
                >
                  Clear
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  startIcon={loading ? <InlineSpinner size={20} /> : null}
                >
                  {loading ? 'Submitting...' : 'Submit Feedback'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
          </>
        )}

        {isAdminOrSuperAdmin && activeTab === 1 && (
          <Box>
            {listActionError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setListActionError(null)}>
                {listActionError}
              </Alert>
            )}
            {loadingList ? (
              <Box sx={{ px: 2, py: 3 }}>
                <ListLinesSkeleton lines={10} />
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
                  <TextField
                    size="small"
                    placeholder="Search by subject, user, category, feedback..."
                    value={feedbackFilterSearch}
                    onChange={(e) => {
                      setFeedbackFilterSearch(e.target.value);
                      setFeedbackPage(0);
                    }}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1, fontSize: '1.2rem' }} />,
                    }}
                    sx={{ minWidth: 280, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                  />
                  {hasAnyFeedbackFilterActive() && (
                    <Button size="small" variant="outlined" onClick={handleClearAllFeedbackFilters}>
                      Clear All Filters
                    </Button>
                  )}
                </Box>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{
                    maxHeight: 'min(70vh, 600px)',
                    overflow: 'auto',
                  }}
                >
                  <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', minWidth: 700 }}>
                    <TableHead>
                      <TableRow>
                        {FEEDBACK_TABLE_COLUMNS.map((col) => (
                          <TableCell
                            key={col.id}
                            align={col.id === 'actions' || col.id === 'resolved' ? 'center' : 'left'}
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              color: SEARCE_COLORS.white,
                              backgroundColor: SEARCE_COLORS.primary,
                              minWidth:
                                col.id === 'actions'
                                  ? 88
                                  : col.id === 'resolved'
                                    ? 150
                                    : col.id === 'feedback'
                                      ? 180
                                      : col.id === 'created'
                                        ? 130
                                        : 120,
                              maxWidth: col.id === 'feedback' ? 220 : undefined,
                              width: col.id === 'actions' ? 88 : col.id === 'resolved' ? 150 : undefined,
                              zIndex: 2,
                              position: 'sticky',
                              top: 0,
                              borderBottom: '1px solid #e0e0e0',
                              '&:hover': { backgroundColor: alpha(SEARCE_COLORS.primary, 0.9) },
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography component="span" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                                {col.label}
                              </Typography>
                              {col.filterable && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleFeedbackFilterClick(col.id, e)}
                                  sx={{
                                    color: (feedbackFilterMenus[col.id]?.selectedValues?.length ?? 0) > 0 ? SEARCE_COLORS.warning : SEARCE_COLORS.white,
                                    '&:hover': { backgroundColor: alpha(SEARCE_COLORS.white, 0.15) },
                                    padding: '2px',
                                  }}
                                >
                                  <FilterListIcon fontSize="small" sx={{ fontSize: '0.7rem' }} />
                                </IconButton>
                              )}
                            </Box>
                            {col.filterable && (
                              <ColumnFilter
                                anchorEl={feedbackFilterMenus[col.id]?.anchorEl ?? null}
                                open={Boolean(feedbackFilterMenus[col.id]?.anchorEl)}
                                onClose={() => handleFeedbackFilterClose(col.id)}
                                onSelect={(values) => handleFeedbackFilterSelect(col.id, values)}
                                options={getUniqueValuesForColumn(col.id, getFeedbackRowsForColumnOptions(col.id))}
                                selectedValues={feedbackFilterMenus[col.id]?.selectedValues ?? []}
                                title={col.label}
                                enableSearch
                              />
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredFeedback.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={FEEDBACK_TABLE_COLUMNS.length} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                            {allFeedback.length === 0 ? 'No sign-off requests yet.' : 'No results match your filters.'}
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedFeedback.map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.submitted_at)}</TableCell>
                            <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 120 }}>
                              <Typography variant="body2" fontWeight={500} noWrap title={row.user_name}>
                                {trimWithEllipsis(row.user_name)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap title={row.user_email}>
                                {trimWithEllipsis(row.user_email)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 100 }}>
                              <Typography variant="body2" noWrap>{trimWithEllipsis(row.category)}</Typography>
                            </TableCell>
                            <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 120 }}>
                              <Typography variant="body2" noWrap>{trimWithEllipsis(row.subject)}</Typography>
                            </TableCell>
                            <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 120 }}>
                              <Typography variant="body2" noWrap>{trimWithEllipsis(stripHtml(row.feedback))}</Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ verticalAlign: 'middle', px: 1 }}>
                              {row.resolved_at ? (
                                <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.3, color: 'text.secondary' }} title={row.resolved_at}>
                                  {formatDateTime(row.resolved_at)}
                                </Typography>
                              ) : (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  disabled={resolveMutation.isPending}
                                  onClick={() => resolveMutation.mutate(row.id)}
                                  sx={{ fontSize: '0.7rem', py: 0.25, px: 1, minWidth: 0, textTransform: 'none' }}
                                >
                                  {resolveMutation.isPending && resolveMutation.variables === row.id ? (
                                    <CircularProgress size={16} color="inherit" />
                                  ) : (
                                    'Resolve'
                                  )}
                                </Button>
                              )}
                            </TableCell>
                            <TableCell align="center" sx={{ width: 88 }}>
                              <IconButton size="small" onClick={() => setViewingFeedback(row)} aria-label="View full feedback">
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={filteredFeedback.length}
                  page={feedbackPage}
                  onPageChange={handleFeedbackPageChange}
                  rowsPerPage={feedbackRowsPerPage}
                  onRowsPerPageChange={handleFeedbackRowsPerPageChange}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  sx={{
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { color: 'text.secondary', fontWeight: 500 },
                    '& .MuiTablePagination-select': { color: SEARCE_COLORS.primary, fontWeight: 600 },
                    '& .MuiIconButton-root': { color: SEARCE_COLORS.primary, '&:hover': { backgroundColor: alpha(SEARCE_COLORS.primary, 0.1) } },
                  }}
                />
              </>
            )}
          </Box>
        )}

        <ViewFeedbackModal
          open={!!viewingFeedback}
          onClose={() => setViewingFeedback(null)}
          feedback={viewingFeedback}
        />
      </Paper>
    </Box>
  );
};

export default UserFeedbackPage;
